import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  CircularProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import { getAdminProducts } from "../../../../services/Admin/ProductsAndCategoriesApi";
import { saveProducts, updateProduct, deleteProduct } from "../../../../services/Admin/ProductsApi";
import { getImageUrl } from "../../../../services/indexMediaBaseUrl";
import AddProductsDialog from "./AddProductsDialog";
import EditProductDialog from "./EditProductDialog";
import DeleteProductConfirm from "./DeleteProductConfirm";
import ProductDetailsDialog from "./ProductDetailsDialog";
import InactiveCategoriesProductsTab from "./InactiveCategoriesProductsTab";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const ProductsTap = () => {
  const { i18n } = useTranslation();
  const lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  const isAr = lang.startsWith("ar");
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addProductsOpen, setAddProductsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editInitial, setEditInitial] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // new: product details dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [productsTab, setProductsTab] = useState(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const isInactiveCategoryStatus = (status) => status === 0 || status === "0" || status === false || status === "false";
  const createdAtValue = (value) => {
    if (!value) return 0;
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  const sortProductsByCreatedAt = (list = [], order = "desc") => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
      const diff = createdAtValue(b?.created_at) - createdAtValue(a?.created_at);
      return order === "asc" ? -diff : diff;
    });
  };
  const sortCategoriesByCreatedAt = (list = [], order = "desc") => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
      const diff = createdAtValue(b?.created_at) - createdAtValue(a?.created_at);
      return order === "asc" ? -diff : diff;
    });
  };

  // reusable fetch function so we can refresh after updates
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAdminProducts();
      if (res && res.success && Array.isArray(res.data)) {
        setProducts(sortProductsByCreatedAt(res.data));
      } else {
        setProducts([]);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/login");
        return;
      }
      setError(err.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const getName = (p) => (isAr ? p.name_ar || p.name_en : p.name_en || p.name_ar);
  const getDesc = (p) => (isAr ? p.desc_ar || "" : p.desc_en || "");
  const getImg = (p) =>
    getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "");

  const handleSaveProducts = async (payload) => {
    try {
      const res = await saveProducts(payload);
      const created = res && res.data ? res.data : res;
      // try to extract created products array
      if (created && Array.isArray(created.products)) {
        setProducts((prev) => sortProductsByCreatedAt([...created.products, ...prev]));
      } else if (Array.isArray(created)) {
        setProducts((prev) => sortProductsByCreatedAt([...created, ...prev]));
      }
      return res;
    } catch (err) {
      throw err;
    }
  };

  // update product handler — update local state if server returns updated object,
  // otherwise refresh full list to ensure UI synced.
  const handleUpdateProduct = async (formData) => {
    try {
      const res = await updateProduct(formData);
      const updated = res && res.data ? res.data : res;
      if (updated && updated.id) {
        // try to merge updated fields into existing product
        setProducts((prev) => {
          let found = false;
          const next = prev.map((p) => {
            if (p.id === updated.id) {
              found = true;
              return { ...p, ...updated };
            }
            return p;
          });
          // if not found, prepend the updated item (or refetch below)
          return sortProductsByCreatedAt(found ? next : [updated, ...next]);
        });
      } else {
        // fallback: refresh full list if response doesn't contain updated item
        await fetchProducts();
      }
      setEditOpen(false); // close dialog on success
      return res;
    } catch (err) {
      // bubble up to dialog for display
      throw err;
    }
  };

  // delete handler invoked by confirm dialog
  const handleDeleteProduct = async (id) => {
    try {
      await deleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== Number(id)));
      setDeleteOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      // rethrow so dialog can display error
      throw err;
    }
  };

  const sortedProducts = sortProductsByCreatedAt(products, sortOrder);

  const uniqueCategories = (() => {
    const map = {};
    sortedProducts
      .filter((p) => !isInactiveCategoryStatus(p?.categroy?.status))
      .forEach((p) => {
      const c = p.categroy;
      const id = c?.id ?? "uncategorized";
      if (!map[id]) {
        map[id] = {
          id,
          name_ar: c?.name_ar || "غير مصنف",
          name_en: c?.name_en || "Uncategorized",
          created_at: c?.created_at || null,
        };
      }
      });
    return sortCategoriesByCreatedAt(Object.values(map), sortOrder);
  })();

  const filteredProducts = sortedProducts.filter((p) => {
    if (isInactiveCategoryStatus(p?.categroy?.status)) return false;
    // category filter
    if (filterCategory !== "all") {
      const cid = p.categroy?.id ?? "uncategorized";
      if (String(cid) !== String(filterCategory)) return false;
    }
    // search filter (check name/desc and category names)
    if (search && search.trim().length > 0) {
      const q = search.trim().toLowerCase();
      const fields = [
        p.name_ar, p.name_en, p.desc_ar, p.desc_en,
        p.categroy?.name_ar, p.categroy?.name_en
      ].filter(Boolean).map((s) => String(s).toLowerCase());
      return fields.some((f) => f.includes(q));
    }
    return true;
  });

  const inactiveCategoryProductsAll = sortedProducts.filter((p) => isInactiveCategoryStatus(p?.categroy?.status));

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Select
            size="small"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">كل الفئات</MenuItem>
            {uniqueCategories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                { (isAr ? c.name_ar : c.name_en) || c.name_en || c.name_ar }
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            placeholder="ابحث عن منتجات..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 320 }}
          />
          <Button
            variant="outlined"
            onClick={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
            sx={{ whiteSpace: "nowrap" }}
          >
            {sortOrder === "desc" ? "الأحدث -> الأقدم" : "الأقدم -> الأحدث"}
          </Button>
        </Box>
        <Button variant="contained" onClick={() => setAddProductsOpen(true)}>
          إضافة منتجات
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={productsTab} onChange={(e, v) => setProductsTab(v)}>
          <Tab label="كل المنتجات" />
          <Tab label="منتجات فئات غير نشطة" />
        </Tabs>
      </Box>

      <AddProductsDialog
        open={addProductsOpen}
        onClose={() => setAddProductsOpen(false)}
        onSubmit={handleSaveProducts}
      />
      <EditProductDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={handleUpdateProduct}
        initialData={editInitial}
      />
      <DeleteProductConfirm
        open={deleteOpen}
        product={deleteTarget}
        onClose={() => { setDeleteOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteProduct}
      />
      {/* product details dialog */}
      <ProductDetailsDialog open={detailOpen} productId={detailId} onClose={() => { setDetailOpen(false); setDetailId(null); }} />

      {productsTab === 1 ? (
        <InactiveCategoriesProductsTab
          products={inactiveCategoryProductsAll}
          isAr={isAr}
          sortOrder={sortOrder}
          getName={getName}
          getDesc={getDesc}
          getImg={getImg}
          onOpenDetails={(id) => {
            setDetailId(id);
            setDetailOpen(true);
          }}
          onOpenEdit={(p) => {
            setEditInitial(p);
            setEditOpen(true);
          }}
          onOpenDelete={(p) => {
            setDeleteTarget(p);
            setDeleteOpen(true);
          }}
        />
      ) : (
        (() => {
          const grouped = filteredProducts.reduce((acc, p) => {
            const catId = p.categroy?.id ?? "uncategorized";
            const catName = isAr
              ? p.categroy?.name_ar || p.categroy?.name_en || "غير مصنف"
              : p.categroy?.name_en || p.categroy?.name_ar || "Uncategorized";
            if (!acc[catId]) {
              acc[catId] = {
                id: catId,
                name: catName,
                created_at: p?.categroy?.created_at || null,
                items: [],
              };
            }
            acc[catId].items.push(p);
            return acc;
          }, {});

          const sortedGroups = sortCategoriesByCreatedAt(Object.values(grouped), sortOrder);

          return sortedGroups.map((group) => (
            <Box key={group.id} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                {group.name}
              </Typography>
              <Grid container spacing={3}>
                {group.items.map((p) => (
                  <Grid item xs={12} sm={6} md={3} key={p.id}>
                    <Card sx={{ height: "100%", transition: "transform .18s", "&:hover": { transform: "translateY(-6px)", boxShadow: 4 } }}>
                      <CardActionArea onClick={() => { setDetailId(p.id); setDetailOpen(true); }} sx={{ textAlign: isAr ? "right" : "left", cursor: "pointer" }}>
                        <CardMedia component="img" image={getImg(p)} alt={getName(p)} sx={{ height: 160, objectFit: "cover", backgroundColor: "#f5f5f5" }} />
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {getName(p)}
                          </Typography>
                          {getDesc(p) ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {getDesc(p)}
                            </Typography>
                          ) : null}
                          {p.categroy && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              {isAr ? p.categroy.name_ar : p.categroy.name_en}
                            </Typography>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setEditInitial(p);
                                setEditOpen(true);
                              }}
                            >
                              تعديل
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ ml: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setDeleteTarget(p);
                                setDeleteOpen(true);
                              }}
                            >
                              حذف
                            </Button>
                          </Box>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ));
        })()
      )}
    </Box>
  );
};

export default ProductsTap;

